/**
 * Cross-cutting scalars. Everything else in `types/` builds on this file, so it
 * must not import from any other domain module.
 *
 * Only plain data lives here. The zod counterparts are in `./schemas/primitives`
 * because a validator is a runtime value: a screen that merely paints a label
 * would otherwise download the whole validation engine to do it.
 */

/** ISO-8601 instant, always serialised in UTC: `2026-08-07T13:45:00.000Z`. */
export type Timestamp = string;

export type Id = string;

export const CURRENCIES = ['CLP', 'USD', 'UF'] as const;
export type Currency = (typeof CURRENCIES)[number];

/** Chart series tokens from `theme.css`; the only colours a stage may name. */
export const CHART_COLOR_TOKENS = [
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'chart-6',
  'chart-7',
  'chart-8',
] as const;
export type ChartColorToken = (typeof CHART_COLOR_TOKENS)[number];

export const SERIE_COLOR_TOKENS = [
  'serie-1',
  'serie-2',
  'serie-3',
  'serie-4',
  'serie-5',
  'serie-6',
  'serie-7',
  'serie-8',
] as const;
export type SerieColorToken = (typeof SERIE_COLOR_TOKENS)[number];

/** Where a person entered the CRM. Shared by Contact and Lead. */
export const SOURCES = [
  'whatsapp_inbound',
  'web_form',
  'voice_agent',
  'import',
  'api',
  'manual',
  'referral',
  'campaign',
  'unknown',
] as const;
export type Source = (typeof SOURCES)[number];

export const SOURCES_TO_PICK = SOURCES.filter((source) => source !== 'api');

export const sourcesToPick = (current: Source): readonly Source[] =>
  current === 'api' ? SOURCES : SOURCES_TO_PICK;

export const SOURCE_LABELS: Record<Source, string> = {
  whatsapp_inbound: 'WhatsApp entrante',
  web_form: 'Formulario web',
  voice_agent: 'Agente de voz',
  import: 'Importación',
  api: 'API',
  manual: 'Carga manual',
  referral: 'Referido',
  campaign: 'Campaña',
  unknown: 'Sin origen',
};

/** Records an Activity, Task or Note can hang from. */
export const SUBJECT_TYPES = ['lead', 'deal', 'contact', 'company'] as const;
export type SubjectType = (typeof SUBJECT_TYPES)[number];

/** Entities that accept user-defined fields. */
export const CUSTOM_FIELD_ENTITIES = ['lead', 'deal', 'contact', 'company'] as const;
export type CustomFieldEntity = (typeof CUSTOM_FIELD_ENTITIES)[number];

export type CustomFieldValue = string | number | boolean | string[] | null;
export type CustomFields = Record<string, CustomFieldValue>;
